// Tablero común (primer uso: Inventario de activos). Tabla con edición directa en las celdas autorizadas, grupos por un
// campo (color y conteo, plegables), filtros combinables, búsqueda mientras se escribe, filtros guardados por usuario,
// selección múltiple con acciones masivas autorizadas (con confirmación), tarjetas de resumen que filtran y panel lateral
// de detalle (PanelLateral) sin salir del tablero. Ergonomía: guardado visible, color siempre con texto, teclado y
// 360 px (las filas pasan a tarjetas).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  agrupar, alternar, alternarTodas, aplicarFiltros, configDesdeGuardado, filtrosActivos,
  type Agrupacion, type ColumnaTablero, type ConfigTablero, type EdicionCelda, type EstadoFiltros, type FiltroDef,
} from './tablero-logica';
import { borrarFiltro, cargarFiltros, guardarFiltro, type FiltroGuardado } from './filtros-guardados';
import './Tablero.css';

export type EstadoGuardado = 'guardando' | 'guardado' | 'error' | null;

/** Texto visible del guardado ("Guardando…", "Guardado", "Error al guardar"). */
export function GuardadoVisible({ estado, detalle }: { estado: EstadoGuardado; detalle?: string }) {
  if (!estado) return <span className="tab-guardado" aria-live="polite" />;
  const texto = estado === 'guardando' ? 'Guardando…' : estado === 'guardado' ? 'Guardado' : 'Error al guardar';
  return (
    <span className={`tab-guardado ${estado}`} role={estado === 'error' ? 'alert' : 'status'} aria-live="polite">
      {texto}{estado === 'error' && detalle ? `: ${detalle}` : ''}
    </span>
  );
}

/** Etiqueta con color: el color siempre va con su texto. */
export function Etiqueta({ texto, color }: { texto: string; color: string }) {
  return <span className="tab-etiqueta" style={{ borderColor: color, color }}><i style={{ background: color }} aria-hidden="true" />{texto}</span>;
}

/** Panel lateral: se cierra con Escape o con el botón; el foco entra al abrirlo y vuelve al cerrarlo. */
export function PanelLateral({ titulo, subtitulo, onCerrar, children, ancho = 560 }: {
  titulo: string; subtitulo?: string; onCerrar: () => void; children: React.ReactNode; ancho?: number;
}) {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const previo = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    const tecla = (e: KeyboardEvent) => { if (e.key === 'Escape') onCerrar(); };
    window.addEventListener('keydown', tecla);
    return () => { window.removeEventListener('keydown', tecla); previo?.focus?.(); };
  }, [onCerrar]);
  return (
    <div className="tab-panel-fondo" onMouseDown={e => { if (e.target === e.currentTarget) onCerrar(); }}>
      <aside ref={ref} className="tab-panel" role="dialog" aria-modal="true" aria-label={titulo} tabIndex={-1} style={{ maxWidth: ancho }}>
        <header className="tab-panel-cabeza">
          <div>
            <h3>{titulo}</h3>
            {subtitulo && <p>{subtitulo}</p>}
          </div>
          <button type="button" className="tab-btn" onClick={onCerrar} aria-label="Cerrar el panel">Cerrar</button>
        </header>
        <div className="tab-panel-cuerpo">{children}</div>
      </aside>
    </div>
  );
}

export interface TarjetaResumen { clave: string; etiqueta: string; valor: number; activa: boolean; color?: string; onClick: () => void }

export interface AccionMasivaTablero {
  clave: string;
  etiqueta: string;
  peligro?: boolean;
  /** Formulario propio de la acción (por ejemplo, elegir el responsable); trae su propio botón de confirmar. */
  formulario?: (ids: string[], cerrar: () => void) => React.ReactNode;
  /** Pregunta de confirmación antes de ejecutar. */
  confirmar?: (n: number) => string;
  ejecutar?: (ids: string[]) => Promise<string | void>;
}

interface Props<T> {
  board: string;
  companyId: string;
  userId: string | null;
  filas: T[];
  idDe: (f: T) => string;
  columnas: ColumnaTablero<T>[];
  /** Contenido de la celda (por defecto, el texto). */
  celda?: (f: T, c: ColumnaTablero<T>) => React.ReactNode;
  filtros: FiltroDef<T>[];
  estadoFiltros: EstadoFiltros;
  onFiltros: (e: EstadoFiltros) => void;
  busqueda: string;
  onBusqueda: (s: string) => void;
  agrupaciones: Agrupacion<T>[];
  grupo: string;
  onGrupo: (g: string) => void;
  grupoPorDefecto: string;
  tarjetas?: TarjetaResumen[];
  puedeEditar: boolean;
  onEditar?: (id: string, clave: string, valor: string) => Promise<void>;
  onAbrir: (id: string) => void;
  abierto?: string | null;
  accionesMasivas?: AccionMasivaTablero[];
  /** Estado vacío (sin ninguna fila): instrucciones para empezar. */
  vacio: React.ReactNode;
  etiquetaFila: (f: T) => string;
  nombreUnidad?: [string, string];
}

export default function Tablero<T>(p: Props<T>) {
  const { filas, columnas, filtros, estadoFiltros, busqueda, agrupaciones, grupo, idDe } = p;
  const [seleccion, setSeleccion] = useState<Set<string>>(new Set());
  const [plegados, setPlegados] = useState<Set<string>>(new Set());
  const [verFiltros, setVerFiltros] = useState(false);
  const [editando, setEditando] = useState<{ id: string; clave: string; valor: string; original: string } | null>(null);
  const enCurso = useRef(false);
  const [guardado, setGuardado] = useState<{ estado: EstadoGuardado; detalle?: string; celda?: string }>({ estado: null });
  const [accion, setAccion] = useState<AccionMasivaTablero | null>(null);
  const [mensajeAccion, setMensajeAccion] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [guardados, setGuardados] = useState<FiltroGuardado[]>([]);
  const [nombreFiltro, setNombreFiltro] = useState('');
  const [errorFiltro, setErrorFiltro] = useState('');
  const [singular, plural] = p.nombreUnidad ?? ['fila', 'filas'];
  const cuenta = (n: number) => `${n} ${n === 1 ? singular : plural}`;

  const recargarGuardados = useCallback(async () => setGuardados(await cargarFiltros(p.companyId, p.userId, p.board)), [p.companyId, p.userId, p.board]);
  useEffect(() => { recargarGuardados(); }, [recargarGuardados]);

  const visibles = useMemo(() => aplicarFiltros(filas, filtros, estadoFiltros, busqueda, columnas), [filas, filtros, estadoFiltros, busqueda, columnas]);
  const ag = agrupaciones.find(a => a.clave === grupo) ?? null;
  const grupos = useMemo(() => agrupar(visibles, ag), [visibles, ag]);
  const idsVisibles = useMemo(() => visibles.map(idDe), [visibles, idDe]);
  // La selección solo guarda filas que siguen existiendo.
  useEffect(() => {
    const ids = new Set(filas.map(idDe));
    setSeleccion(s => { const n = new Set(Array.from(s).filter(id => ids.has(id))); return n.size === s.size ? s : n; });
  }, [filas, idDe]);
  const elegidas = Array.from(seleccion).filter(id => idsVisibles.includes(id));
  const nActivos = filtrosActivos(estadoFiltros) + (busqueda.trim() ? 1 : 0);
  const plantilla = `40px ${columnas.map(c => `minmax(0, ${c.ancho ?? 1}fr)`).join(' ')}`;

  const guardarCelda = async () => {
    if (!editando || !p.onEditar || enCurso.current) return;
    const { id, clave, valor, original } = editando;
    setEditando(null);
    if (valor === original) return;
    enCurso.current = true;
    setGuardado({ estado: 'guardando', celda: `${id}:${clave}` });
    try {
      await p.onEditar(id, clave, valor);
      setGuardado({ estado: 'guardado', celda: `${id}:${clave}` });
    } catch (e) {
      setGuardado({ estado: 'error', detalle: e instanceof Error ? e.message : String(e), celda: `${id}:${clave}` });
    } finally { enCurso.current = false; }
  };

  const ejecutarAccion = async (a: AccionMasivaTablero) => {
    if (!a.ejecutar) return;
    setOcupado(true); setMensajeAccion('');
    try {
      const m = await a.ejecutar(elegidas);
      setMensajeAccion(m || 'Listo.');
      setAccion(null);
      if (a.clave !== 'exportar') setSeleccion(new Set());
    } catch (e) { setMensajeAccion(e instanceof Error ? e.message : String(e)); }
    setOcupado(false);
  };

  const elegirAccion = (a: AccionMasivaTablero) => {
    setMensajeAccion('');
    if (!a.formulario && !a.confirmar) { ejecutarAccion(a); return; }
    setAccion(a);
  };

  const aplicarGuardado = (g: FiltroGuardado) => {
    const c = configDesdeGuardado(g.config, filtros, agrupaciones, p.grupoPorDefecto);
    p.onFiltros(c.filtros); p.onBusqueda(c.busqueda); p.onGrupo(c.grupo);
  };
  const guardarActual = async () => {
    if (!p.userId) return;
    setErrorFiltro('');
    try {
      const config: ConfigTablero = { filtros: estadoFiltros, busqueda, grupo };
      await guardarFiltro(p.companyId, p.userId, p.board, nombreFiltro, config);
      setNombreFiltro('');
      await recargarGuardados();
    } catch (e) { setErrorFiltro(e instanceof Error ? e.message : String(e)); }
  };

  if (filas.length === 0) return <div className="tab">{p.vacio}</div>;

  const editor = (f: T, c: ColumnaTablero<T>, ed: EdicionCelda) => {
    const cerrar = () => setEditando(null);
    const alTeclear = (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); cerrar(); }
      if (e.key === 'Enter') { e.preventDefault(); guardarCelda(); }
    };
    if (ed.tipo === 'opcion') {
      return (
        <select autoFocus aria-label={`${c.titulo} de ${p.etiquetaFila(f)}`} value={editando?.valor ?? ''}
          onChange={e => setEditando(x => x && { ...x, valor: e.target.value })} onKeyDown={alTeclear} onBlur={guardarCelda}>
          {ed.vacio !== undefined && <option value="">{ed.vacio}</option>}
          {ed.opciones.map(o => <option key={o.valor} value={o.valor}>{o.etiqueta}</option>)}
        </select>
      );
    }
    return (
      <input autoFocus type="text" aria-label={`${c.titulo} de ${p.etiquetaFila(f)}`} maxLength={ed.max ?? 160} value={editando?.valor ?? ''}
        onChange={e => setEditando(x => x && { ...x, valor: e.target.value })} onKeyDown={alTeclear} onBlur={guardarCelda} />
    );
  };

  return (
    <div className="tab">
      {p.tarjetas && (
        <div className="tab-tarjetas" role="group" aria-label="Resumen (clic para filtrar)">
          {p.tarjetas.map(t => (
            <button key={t.clave} type="button" className={`tab-tarjeta${t.activa ? ' on' : ''}`} aria-pressed={t.activa} onClick={t.onClick}>
              <b style={t.color ? { color: t.color } : undefined}>{t.valor}</b><span>{t.etiqueta}</span>
            </button>
          ))}
        </div>
      )}

      <div className="tab-barra">
        <input type="search" className="tab-buscar" placeholder="Buscar por código, nombre, ubicación…" aria-label="Buscar"
          value={busqueda} onChange={e => p.onBusqueda(e.target.value)} />
        <button type="button" className={`tab-btn${verFiltros ? ' on' : ''}`} aria-expanded={verFiltros} onClick={() => setVerFiltros(v => !v)}>
          Filtros{nActivos ? ` (${nActivos})` : ''}
        </button>
        {agrupaciones.length > 0 && (
          <label className="tab-agrupar">
            <span>Agrupar por</span>
            <select value={grupo} onChange={e => p.onGrupo(e.target.value)}>
              <option value="">Sin grupos</option>
              {agrupaciones.map(a => <option key={a.clave} value={a.clave}>{a.titulo}</option>)}
            </select>
          </label>
        )}
        {nActivos > 0 && <button type="button" className="tab-btn" onClick={() => { p.onFiltros({}); p.onBusqueda(''); }}>Quitar filtros</button>}
        <GuardadoVisible estado={guardado.estado} detalle={guardado.detalle} />
      </div>

      {verFiltros && (
        <div className="tab-filtros">
          {filtros.map(d => (
            <fieldset key={d.clave}>
              <legend>{d.titulo}</legend>
              <div className="tab-chips">
                {d.opciones.map(o => {
                  const on = (estadoFiltros[d.clave] ?? []).includes(o.valor);
                  return (
                    <button key={o.valor} type="button" className={`tab-chip${on ? ' on' : ''}`} aria-pressed={on}
                      onClick={() => p.onFiltros(alternar(estadoFiltros, d.clave, o.valor))}>{o.etiqueta}</button>
                  );
                })}
              </div>
            </fieldset>
          ))}
          <fieldset>
            <legend>Mis filtros guardados</legend>
            {guardados.length === 0 && <p className="tab-nota">Aún no guardas filtros. Elige filtros y guárdalos con un nombre; solo tú los ves.</p>}
            <div className="tab-chips">
              {guardados.map(g => (
                <span key={g.id} className="tab-guardado-item">
                  <button type="button" className="tab-chip" onClick={() => aplicarGuardado(g)}>{g.name}</button>
                  <button type="button" className="tab-quitar" aria-label={`Borrar el filtro ${g.name}`}
                    onClick={async () => { try { await borrarFiltro(g.id); await recargarGuardados(); } catch (e) { setErrorFiltro(e instanceof Error ? e.message : String(e)); } }}>×</button>
                </span>
              ))}
            </div>
            {p.userId && (
              <div className="tab-guardar-filtro">
                <input type="text" maxLength={80} placeholder="Nombre del filtro" aria-label="Nombre del filtro" value={nombreFiltro}
                  onChange={e => setNombreFiltro(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') guardarActual(); }} />
                <button type="button" className="tab-btn" disabled={!nombreFiltro.trim()} onClick={guardarActual}>Guardar filtro actual</button>
              </div>
            )}
            {errorFiltro && <p className="tab-error" role="alert">{errorFiltro}</p>}
          </fieldset>
        </div>
      )}

      {elegidas.length > 0 && p.accionesMasivas && p.accionesMasivas.length > 0 && (
        <div className="tab-masivas" role="region" aria-label="Acciones con la selección">
          <span><b>{cuenta(elegidas.length)}</b> {elegidas.length === 1 ? 'seleccionada' : 'seleccionadas'}</span>
          {p.accionesMasivas.map(a => (
            <button key={a.clave} type="button" className={`tab-btn${a.peligro ? ' peligro' : ''}${accion?.clave === a.clave ? ' on' : ''}`}
              disabled={ocupado} onClick={() => elegirAccion(a)}>{a.etiqueta}</button>
          ))}
          <button type="button" className="tab-btn" onClick={() => { setSeleccion(new Set()); setAccion(null); }}>Quitar selección</button>
          {accion?.formulario && <div className="tab-masiva-form">{accion.formulario(elegidas, () => setAccion(null))}</div>}
          {accion?.confirmar && (
            <div className="tab-confirmar" role="alertdialog" aria-label="Confirmar la acción">
              <p>{accion.confirmar(elegidas.length)}</p>
              <button type="button" className={`tab-btn ${accion.peligro ? 'peligro' : 'primario'}`} disabled={ocupado} onClick={() => ejecutarAccion(accion)}>
                {ocupado ? 'Aplicando…' : `Sí, ${accion.etiqueta.toLowerCase()}`}
              </button>
              <button type="button" className="tab-btn" onClick={() => setAccion(null)}>Cancelar</button>
            </div>
          )}
        </div>
      )}
      {mensajeAccion && <p className="tab-mensaje" role="status">{mensajeAccion}</p>}

      <p className="tab-nota" aria-live="polite">
        {visibles.length === filas.length ? cuenta(filas.length) : `${cuenta(visibles.length)} de ${filas.length}`}
      </p>
      {visibles.length === 0 ? (
        <div className="tab-vacio-filtro">
          <p>Ningún resultado con estos filtros.</p>
          <button type="button" className="tab-btn" onClick={() => { p.onFiltros({}); p.onBusqueda(''); }}>Quitar filtros</button>
        </div>
      ) : (
        <div className="tab-tabla" role="table" aria-label="Tablero">
          <div className="tab-fila cabeza" role="row" style={{ gridTemplateColumns: plantilla }}>
            <span role="columnheader">
              <input type="checkbox" aria-label="Seleccionar todas las visibles" checked={idsVisibles.length > 0 && idsVisibles.every(id => seleccion.has(id))}
                onChange={() => setSeleccion(s => alternarTodas(s, idsVisibles))} />
            </span>
            {columnas.map(c => <span key={c.clave} role="columnheader" className={c.ocultarEnMovil ? 'tab-movil-oculta' : undefined}>{c.titulo}</span>)}
          </div>
          {grupos.map(g => {
            const plegado = plegados.has(g.def.valor);
            return (
              <div key={g.def.valor} role="rowgroup" className="tab-grupo">
                {ag && (
                  <button type="button" className="tab-grupo-cabeza" aria-expanded={!plegado} style={{ borderLeftColor: g.def.color }}
                    onClick={() => setPlegados(s => { const n = new Set(s); if (n.has(g.def.valor)) n.delete(g.def.valor); else n.add(g.def.valor); return n; })}>
                    <span aria-hidden="true">{plegado ? '▸' : '▾'}</span>
                    <Etiqueta texto={g.def.etiqueta} color={g.def.color} />
                    <span className="tab-conteo">{cuenta(g.filas.length)}</span>
                  </button>
                )}
                {!plegado && g.filas.map(f => {
                  const id = idDe(f);
                  return (
                    <div key={id} role="row" className={`tab-fila${p.abierto === id ? ' abierta' : ''}${seleccion.has(id) ? ' elegida' : ''}`}
                      style={{ gridTemplateColumns: plantilla, borderLeftColor: ag ? g.def.color : undefined }}>
                      <span role="cell">
                        <input type="checkbox" aria-label={`Seleccionar ${p.etiquetaFila(f)}`} checked={seleccion.has(id)}
                          onChange={() => setSeleccion(s => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; })} />
                      </span>
                      {columnas.map((c, i) => {
                        const ed = p.puedeEditar && p.onEditar && c.edicion ? c.edicion(f) : null;
                        const esta = editando?.id === id && editando.clave === c.clave;
                        const clave = `${id}:${c.clave}`;
                        const contenido = p.celda ? p.celda(f, c) : c.texto(f) || '—';
                        return (
                          <span key={c.clave} role="cell" className={`tab-celda${c.ocultarEnMovil ? ' tab-movil-oculta' : ''}`}>
                            <em className="tab-et">{c.titulo}</em>
                            {i === 0 ? (
                              <button type="button" className="tab-abrir" onClick={() => p.onAbrir(id)} aria-label={`Abrir ${p.etiquetaFila(f)}`}>{contenido}</button>
                            ) : esta && ed ? editor(f, c, ed) : ed ? (
                              <button type="button" className="tab-editable" title="Clic para editar"
                                onClick={() => { const v = c.valorEdicion ? c.valorEdicion(f) : c.texto(f); setEditando({ id, clave: c.clave, valor: v, original: v }); }}>
                                {contenido}
                                {guardado.celda === clave && guardado.estado === 'guardando' && <small> Guardando…</small>}
                                {guardado.celda === clave && guardado.estado === 'error' && <small className="tab-error"> Error al guardar</small>}
                              </button>
                            ) : contenido}
                          </span>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
